/**
 * User-Authored Evidence (UAE) — type definitions for per-requirement
 * authored evidence.
 *
 * Coverage Matrix today offers only an `[UPLOAD]` action on each
 * checklist requirement. UAE adds a parallel `[AUTHOR]` action that
 * lets users record what they already know (verbal verification with
 * the city, BFE from FEMA, parcel id from the title abstract, …)
 * without uploading a screenshot. These types define the per-field
 * schema rendered in the AuthorEvidenceDialog and the form state
 * captured when the user clicks Save.
 *
 * Persistence reuses `ExtractedClaim` (Option A — see the UAE prompt
 * pack `docs/prompt-packs/User_Authored_Evidence_Prompt.md` §3) so the
 * authored payload flows automatically through the existing LLM prompt
 * (`buildSiteContextBlock`), SED binding registry, conflict surface,
 * and rerun-diff layer. The new fields on `ExtractedClaim`
 * (`requirementId`, `authoredKind`, `structuredFields`,
 * `priorVersions`) are declared in `src/types/siteContext.ts`.
 */

/**
 * Supported input controls for a single authored-evidence field. The
 * dialog renders the appropriate control per type:
 *   - `'number'`   → `<input type="number">` with optional `unit` suffix
 *   - `'string'`   → `<input type="text">`
 *   - `'date'`     → `<input type="date">` (ISO yyyy-mm-dd stored)
 *   - `'enum'`     → `<select>` populated from `enumValues`
 *   - `'list'`     → comma-separated text input (stored as joined string)
 *   - `'textarea'` → multi-line textarea
 *   - `'boolean'`  → toggle / checkbox
 */
export type AuthoredFieldType =
  | 'number'
  | 'string'
  | 'date'
  | 'enum'
  | 'list'
  | 'textarea'
  | 'boolean';

/**
 * Schema for a single field rendered in the AuthorEvidenceDialog. The
 * complete schema for a requirement lives on
 * `ChecklistItem.authoredSchema` (in `documentChecklists.ts`) so the
 * dialog can look it up by `requirementId`.
 */
import type { SiteEntity } from './site';
import type { ProjectIntent } from './siteRag';
import type { SiteEvidenceDocument } from './siteEvidence';

/** Inputs for schema-level auto-derivation of report setup fields. */
export interface AuthoredFieldDeriveContext {
  site: SiteEntity;
  projectIntent?: ProjectIntent;
  sed?: SiteEvidenceDocument | null;
}

export interface AuthoredFieldDef {
  /** Stable key used in `structuredFields[key]`. */
  key: string;
  /** Display label rendered next to the input. */
  label: string;
  type: AuthoredFieldType;
  /** When true, the inferred confidence will not return `'high'` unless this field is populated. */
  required?: boolean;
  /** Optional unit suffix rendered next to the input (e.g. `'kPa'`, `'m'`, `'MW'`). */
  unit?: string;
  /** Enum values — required when `type === 'enum'`. */
  enumValues?: readonly string[];
  /** Placeholder text rendered in the input. */
  placeholder?: string;
  /** Inline help text rendered under the label. */
  help?: string;
  /**
   * Display-only setup value derived from canonical site data. Read-only
   * fields may be surfaced in report setup, but must never be written into
   * typed preferences as a second source of truth.
   */
  readOnly?: boolean;
  /**
   * Optional auto-derivation. Called when the field is unset and `required: true`.
   * Returns a value, or `undefined` to fall through to inline edit / intent inference.
   * Pure function — receives the inputs it needs, no side effects, no I/O.
   */
  derive?: (ctx: AuthoredFieldDeriveContext) => unknown | undefined;
}

/**
 * Form state captured by AuthorEvidenceDialog. Persisted onto the
 * resulting `ExtractedClaim` as `structuredFields` (the `fields` map),
 * with `notes` appended to the claim text and `confidence` mapped via
 * `confidenceToScore`.
 */
export interface AuthoredEvidenceFormState {
  /** Per-field values keyed by `AuthoredFieldDef.key`. */
  fields: Record<string, string | number | boolean>;
  /**
   * Free-text notes. Always shown (prose-only is a first-class path)
   * so any requirement can be filled even when no structured fields
   * apply or are known.
   */
  notes: string;
  /**
   * Authored confidence band — `'low' | 'medium' | 'high'`. UI defaults
   * to the value returned by `inferAuthoredConfidence`; user can
   * override via a dropdown when they explicitly want to.
   */
  confidence: 'low' | 'medium' | 'high';
}

/**
 * Score mapping for the three confidence bands. The values sit BELOW
 * `DOC_FALLBACK_CONFIDENCE` (0.8) so authored evidence never outranks a
 * doc-extracted slot of the same provenance class — by design (see
 * UAE prompt-pack §3, "system-inferred confidence prevents users from
 * defaulting everything to `high` and corrupting the resolver").
 */
export const AUTHORED_CONFIDENCE_SCORE: Record<'low' | 'medium' | 'high', number> = {
  low: 0.45,
  medium: 0.6,
  high: 0.75,
};

/**
 * Map a confidence band back to its numeric score for persistence on
 * `ExtractedClaim.extractorConfidence`. Re-exported via
 * `authoredEvidenceWriter.confidenceToScore` for downstream callers.
 */
export function confidenceToScore(band: 'low' | 'medium' | 'high'): number {
  return AUTHORED_CONFIDENCE_SCORE[band];
}

/**
 * Map a numeric score back to its confidence band — used by readers
 * who want to surface the band label on an already-persisted claim
 * (e.g. the Coverage Matrix tooltip and the SED status palette).
 *
 * Thresholds picked to be inclusive of the AUTHORED_CONFIDENCE_SCORE
 * values exactly and to round in the user-friendly direction (0.5 →
 * medium, not low).
 */
export function scoreToConfidence(score: number): 'low' | 'medium' | 'high' {
  if (score >= AUTHORED_CONFIDENCE_SCORE.high) return 'high';
  if (score >= AUTHORED_CONFIDENCE_SCORE.medium) return 'medium';
  return 'low';
}

/**
 * Test whether a form value counts as "populated" for inference +
 * persistence. Empty strings, `undefined`, and explicit `null` are
 * treated as unset; `0` and any non-empty string ARE treated as set
 * so a user can author "Bearing capacity = 0 kPa" without the field
 * being silently ignored. Booleans behave conservatively — `true`
 * counts as filled but `false` does NOT, so a freshly-rendered
 * dialog (which defaults boolean fields to `false`) doesn't read
 * as "structured" until the user explicitly toggles the box.
 */
export function isFieldFilled(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value === true;
  return true;
}

/**
 * Whether a persisted authored value is present for SED overlay / report
 * display. Unlike {@link isFieldFilled}, explicit `false` booleans count
 * as present once saved (e.g. "Critical habitat: No").
 */
export function isAuthoredValuePresent(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length > 0;
  return true;
}

/**
 * Infer the authored-evidence confidence band from the form state and
 * schema. The default surfaces in the dialog as e.g.
 * `Default: medium (3/5 fields filled).`
 *
 * Rules (deliberately conservative so the user has to opt into `high`):
 *   - `'high'`   ⇔ every `required: true` field is filled AND ≥80% of
 *     all fields are filled.
 *   - `'medium'` ⇔ ≥40% of all fields are filled.
 *   - `'low'`    ⇔ otherwise (including prose-only with only `notes`).
 *
 * Edge cases:
 *   - Empty schema (prose-only requirement, no structured fields) — band
 *     is `'low'` unless `notes` is filled, in which case `'medium'`. The
 *     `notes` field is treated separately because it lives on the form
 *     state, not in the field schema.
 */
export function inferAuthoredConfidence(
  form: AuthoredEvidenceFormState,
  schema: readonly AuthoredFieldDef[],
): 'low' | 'medium' | 'high' {
  const hasNotes = isFieldFilled(form.notes);
  if (schema.length === 0) {
    return hasNotes ? 'medium' : 'low';
  }
  const requiredFields = schema.filter(f => f.required);
  const requiredFilled = requiredFields.filter(f => isFieldFilled(form.fields[f.key])).length;
  const allFilled = schema.filter(f => isFieldFilled(form.fields[f.key])).length;
  const overallFraction = allFilled / schema.length;
  const requiredAllFilled = requiredFields.length === 0 || requiredFilled === requiredFields.length;
  if (requiredAllFilled && overallFraction >= 0.8) return 'high';
  if (overallFraction >= 0.4 || (hasNotes && allFilled >= 1)) return 'medium';
  if (hasNotes) return 'low';
  return 'low';
}

/**
 * Make an empty form state seeded by a schema. Boolean fields default
 * to `false`; other types default to empty string so the input
 * components render predictably.
 */
export function makeEmptyForm(schema: readonly AuthoredFieldDef[]): AuthoredEvidenceFormState {
  const fields: Record<string, string | number | boolean> = {};
  for (const f of schema) {
    fields[f.key] = f.type === 'boolean' ? false : '';
  }
  return { fields, notes: '', confidence: 'low' };
}

/**
 * True when the form has at least one populated structured field.
 * Drives `authoredKind: 'structured' | 'prose'` on the persisted claim.
 */
export function hasAnyStructuredField(
  form: AuthoredEvidenceFormState,
  schema: readonly AuthoredFieldDef[],
): boolean {
  return schema.some(f => isFieldFilled(form.fields[f.key]));
}

/**
 * UAE-FOLLOWUP-SCHEMAS — per-schema completeness contract.
 *
 * Partitions a schema's `AuthoredFieldDef[]` into its required vs.
 * optional buckets. Mirrors the same `f.required === true` predicate
 * `inferAuthoredConfidence` uses so the two stay in lockstep — pulled
 * out as a tiny helper so schema-completeness pinning tests can assert
 * the partition deterministically without re-implementing the filter.
 *
 * NOTES_FIELD (declared in `documentChecklists.ts`) is always
 * `required: false`, so it lands in `optionalFields` for every schema —
 * which means the high-confidence projection in
 * `confidenceFromCompleteness` cleanly rewards a user who fills the
 * required fact AND adds context notes.
 */
export interface SchemaCompletenessProjection {
  requiredFields: readonly AuthoredFieldDef[];
  optionalFields: readonly AuthoredFieldDef[];
}

export function partitionSchemaFields(
  schema: readonly AuthoredFieldDef[],
): SchemaCompletenessProjection {
  const requiredFields: AuthoredFieldDef[] = [];
  const optionalFields: AuthoredFieldDef[] = [];
  for (const f of schema) {
    if (f.required) requiredFields.push(f);
    else optionalFields.push(f);
  }
  return { requiredFields, optionalFields };
}

/**
 * UAE-FOLLOWUP-SCHEMAS — schema-authoring contract:
 *
 *   filled < required          → 0.45 (low)
 *   filled === required        → 0.6  (medium)
 *   filled  >  required        → 0.75 (high)  (required + ≥1 optional)
 *
 * Distinct from `inferAuthoredConfidence` — that one runs against a
 * form's `AuthoredEvidenceFormState` and looks at the 80% / 40%
 * boundaries with the `hasNotes` bump. THIS helper is the per-schema
 * field-completeness contract every new requirement schema must satisfy,
 * pinned by the UAE-FOLLOWUP-SCHEMAS tests so we never silently drift
 * from "fill the required fields → medium" into a higher tier.
 *
 * `filled` and `required` are integer counts (NOT booleans / fractions).
 * Inputs are clamped to `[0, Infinity)` so a caller passing `-1` (from
 * a buggy subtraction) doesn't poison the ladder. Out-of-band values
 * collapse to the safest tier (low).
 */
export function confidenceFromCompleteness(
  filled: number,
  required: number,
): 0.45 | 0.6 | 0.75 {
  // Defensive: NaN / negatives collapse to the safest tier.
  if (!Number.isFinite(filled) || !Number.isFinite(required)) return 0.45;
  const f = Math.max(0, Math.floor(filled));
  const r = Math.max(0, Math.floor(required));
  if (f < r) return 0.45;
  if (f === r) return 0.6;
  return 0.75;
}
